import * as React from "react";
import {
  Button,
  Card,
  Container,
  createStyles,
  Grid,
  List,
  ListItem,
  makeStyles,
  Theme,
  Toolbar,
  Tooltip,
  Typography,
} from "@material-ui/core";
import SimpleTable from "../../components/SimpleTable";
import DownloadIcon from "@material-ui/icons/SaveAlt";
import SimpleTextField from "../../components/SimpleTextField";
import { useHubx } from "../../HubX";
import { useSnackbar } from "notistack";
import EClient from "../../core/contracts/EClient";
import EUser from "../../core/contracts/EUser";
import useConstructor from "../../core/hooks/useConstructor";
import MaterialSelect from "../../components/MaterialSelect";
import BaseFilter from "../../core/models/BaseFilter";
import { isArrayValid } from "../../helpers/utility";
import { FETCH_CLIENTS, FILTER_CLIENTS, FETCH_ALL_CLIENTS, FETCH_ALL_CLIENTS_BY_FILTER } from "../../Fedx";
import { sortBy, uniq } from "lodash";
import { ClientServiceFilterParams } from "../../services/ClientsService";
import MaterialAutoComplete from "../../components/MaterialAutoComplete";
import PaginateTable from "../../components/PaginateTable";
import IPagination from "../../core/contracts/IPagination";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      marginTop: theme.spacing(2),
      display: "flex",
      alignItems: "center",
      columnGap: `${theme.spacing(2)}px`,
    },

    buttonWrapper: {
      display: "flex",
      alignItems: "center",
      columnGap: `${theme.spacing(1)}px`,
    },

    card: {
      padding: theme.spacing(1),
      marginTop: theme.spacing(2),
    },

    toolbarWrapper: {
      display: "flex",
      justifyContent: "flex-end",
      flex: 1,
    },
  })
);

const ClientInfoColumns = [
  "Cliente",
  "Código JDE/Código LI",
  "Contato",
  "Unidade Responsável",
  "Gerente de Negócios",
  "Gerente Regional",
  "Diretor / BU",
];

function ClientsList() {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [clients, setClients] = React.useState<EClient[]>([]);
  const [customersOptions, setCustomersOptions] = React.useState<string[]>([]);
  const [clientFilter, setClientFilter] = React.useState<string>("Todos");
  const [codeFilter, setCodeFilter] = React.useState<string>("");
  const [userFilter, setUserFilter] = React.useState<string>("");

  const [unitOptions, setUnitOptions] = React.useState<string[]>([]);
  const [unitFilter, setUnitFilter] = React.useState<string>("all");

  const [pagination, setPagination] = React.useState<IPagination>({
    CurrentPage: 0,
    TotalPages: 0,
    PageSize: 0,
    TotalCount: 0,
  });

  const classes = useStyles();

  const hub = useHubx();
  const toastr = useSnackbar();

  const filterCustomCustomers = async (filter: string) => {
    const clientOptions = await hub.request(FETCH_ALL_CLIENTS_BY_FILTER, filter);
    setCustomersOptions(clientOptions);
  };

  const fetchClientsWithNoFilters = async () => {
    setLoading(true);
    try {
      const APIResponse: EClient[] = await hub.request(FETCH_CLIENTS);

      if (isArrayValid(APIResponse)) {
        let possibleUnits = APIResponse?.map((x) => x?.unidade_responsavel).filter((x) => x) ?? [];
        possibleUnits = uniq(possibleUnits);

        setUnitOptions(possibleUnits as string[]);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      toastr.enqueueSnackbar("Ops! Ocorreu um erro ao buscar a lista de clientes!", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangeClient = React.useCallback(
    (e: React.ChangeEvent<{ value: string; textContent: string }>) => {
      const newValue = e?.target?.textContent;
      if (newValue) {
        setClientFilter(newValue);
      }
    },
    []
  );

  const customerFilterProp = {
    id: "client",
    items: [
      ...sortBy(
        customersOptions.map((x) => {
          return {
            key: x,
            value: x,
          };
        }),
        "key"
      ),
    ],
    style: {
      minWidth: 281,
    },
    label: "Cliente",
    value: clientFilter,
    handleChange: handleChangeClient,
    requestItems: filterCustomCustomers,
    noOptionsText: "Nenhum cliente ou evento associado"
  };

  const loadAllCustomers = async () => {
    setLoading(true);
    try {
      const clientOptions = await hub.request(FETCH_ALL_CLIENTS);
      setCustomersOptions(clientOptions);
    } catch (error) {
      toastr.enqueueSnackbar("Ops! Ocorreu um erro ao buscar a lista de clientes!", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    setLoading(true);
    try {
      const params: ClientServiceFilterParams = {
        client: clientFilter == "Todos" ? "" : clientFilter,
        jdeCode: codeFilter,
        codigoLi: codeFilter,
        unit: unitFilter === "all" ? "" : unitFilter,
        user: userFilter,
        pageSize: pagination.PageSize,
        pageNumber: pagination.CurrentPage + 1,
      };

      const APIResponse = await hub.request(FILTER_CLIENTS, params);
      if (APIResponse) {
        const { items } = APIResponse;
        const newPagination = APIResponse.pagination;

        setPagination(newPagination);

        if (newPagination.CurrentPage == 1) {
          setClients(isArrayValid(items) ? items : []);
        } else {
          setClients(isArrayValid(items) ? [...clients, ...items] : []);
        }
      }

      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      toastr.enqueueSnackbar("Ops! Ocorreu um erro ao buscar a lista de clientes!", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useConstructor(() => {
    fetchClientsWithNoFilters();
    loadAllCustomers();
  });

  const clearFilters = () => {
    setClientFilter("Todos");
    setCodeFilter("");
    setUserFilter("");
    setUnitFilter("all");
  };

  const renderValue = (value?: string | null | number) => {
    return value || value === 0 ? value : "-";
  };

  const buildTooltipFromUser = (user: EUser) => {
    return (
      <List>
        <ListItem>
          <strong>Email:</strong>
          <span>{renderValue(user?.email)}</span>
        </ListItem>
      </List>
    );
  };

  const getUserInfoFromClient = (value?: null | string | EUser) => {
    if (!value) {
      return "-";
    }

    if (typeof value === "string") return value;

    return (
      <Tooltip title={buildTooltipFromUser(value)}>
        <span>{value?.nome}</span>
      </Tooltip>
    );
  };

  const getCodetype = (jdeNumber: string | undefined, liCode: string | undefined) => {
    if (jdeNumber == null && liCode == null) {
      return "-";
    }
    if(liCode != null){
      return (
        <Tooltip title={'Código LI'}>
          <span>{liCode}</span>
        </Tooltip>
      );    
    }
    return (
      <Tooltip title={'Código JDE'}>
        <span>{jdeNumber}</span>
      </Tooltip>
    );
  };

  const fetchFilter = () => {
    pagination.CurrentPage = 0;
    fetchClients();
  };

  const clientsRows =
    clients?.map((client) => {
      return [
        renderValue(client?.nome_fantasia),
        getCodetype(client?.numero_jde, client?.codigo_li),
        renderValue(client?.email),
        renderValue(client?.unidade_responsavel),
        getUserInfoFromClient(client?.codigo_gerente_negocio),
        getUserInfoFromClient(client?.email_gerente_regional),
        getUserInfoFromClient(client?.email_diretor),
      ];
    }) ?? [];

  const unitSelectFilter: BaseFilter = React.useMemo(() => {
    return {
      value: unitFilter,
      handleChange: (e) => setUnitFilter(e.target.value),
      items: [
        {
          key: "all",
          value: <strong>Todos</strong>,
        },
        ...(unitOptions?.map((x) => {
          return {
            key: x,
            value: x,
          };
        }) ?? []),
      ],
      id: "clients.list.unit.select.filter",
      label: "Unidade",
    };
  }, [unitFilter, unitOptions]);

  return (
    <Container maxWidth={"xl"}>
      <Grid container direction="column">
        <div className={classes.root}>
          <MaterialAutoComplete key={customerFilterProp.id} filter={customerFilterProp} />

          <SimpleTextField
            value={codeFilter}
            label="Código JDE/Código LI"
            onChange={(e) => setCodeFilter(e.target.value)}
          />

          <MaterialSelect disabled={!isArrayValid(unitOptions)} filter={unitSelectFilter} />

          <SimpleTextField
            value={userFilter}
            label="Usuário"
            onChange={(e) => setUserFilter(e.target.value)}
          />

          <div className={classes.buttonWrapper}>
            <Button variant="outlined" onClick={clearFilters}>
              Limpar
            </Button>

            <Button variant="contained" onClick={fetchFilter}>
              Pesquisar
            </Button>
          </div>
        </div>

        <Card className={classes.card}>
          <Toolbar>
            <div className={classes.toolbarWrapper}>
              <div>
                <Typography>{`Total de Registros: ${pagination?.TotalCount ?? "-"}`}</Typography>

                {/* <Button>
                  <DownloadIcon />
                  Baixar dados da tabela
                </Button> */}
              </div>
            </div>
          </Toolbar>

          <PaginateTable
            loading={loading}
            header={ClientInfoColumns}
            rows={clientsRows}
            loadMoreItems={fetchClients}
            pagination={pagination}
          />
        </Card>
      </Grid>
    </Container>
  );
}

export default ClientsList;
